/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  TextField,
  Alert,
  AlertTitle,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import { useSnackbar } from "hooks/useSnackbar";
import { generateAdminCredentials } from "services/miscService";
import { decrypt } from "utils/encryption";
import { getEncryptionKeyFromToken } from "utils/jwtDecoder";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    maxWidth: "800px",
    margin: "0 auto",
  },
  paper: {
    padding: "32px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  header: {
    marginBottom: "24px",
  },
  warningBox: {
    marginBottom: "24px",
    padding: "16px",
    backgroundColor: "#fff3cd",
    border: "1px solid #ffc107",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  credentialsBox: {
    marginTop: "24px",
    padding: "20px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "2px solid #dee2e6",
  },
  fieldContainer: {
    marginBottom: "20px",
  },
  copyButton: {
    color: theme.palette.primary.main,
  },
  generateButton: {
    marginTop: "16px",
    width: "100%",
  },
  expiryInfo: {
    marginTop: "16px",
    padding: "12px",
    backgroundColor: "#e3f2fd",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  hiddenValue: {
    letterSpacing: "0.15em",
    fontFamily: "monospace",
  },
}));

const AdminCredentials = () => {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [showInternalKey, setShowInternalKey] = useState(false);

  const handleGenerateCredentials = async () => {
    try {
      setLoading(true);

      // Get the encryption key from the current user's JWT token
      const encryptionKey = getEncryptionKeyFromToken();

      if (!encryptionKey) {
        showSnackbar(
          "Encryption key not found in session. Please log in again.",
          "error"
        );
        setLoading(false);
        return;
      }

      const response = await generateAdminCredentials();

      if (response.status) {
        // Decrypt the credentials using the session-based encryption key
        const { token, internalKey, expiresIn } = response.data;

        const decryptedToken = await decrypt(token, encryptionKey);
        const decryptedInternalKey = await decrypt(internalKey, encryptionKey);

        setCredentials({
          token: decryptedToken,
          internalKey: decryptedInternalKey,
          expiresIn,
        });
        showSnackbar("Admin credentials generated successfully!", "success");
      } else {
        showSnackbar(
          response.message || "Failed to generate credentials",
          "error"
        );
      }
    } catch (error) {
      console.error("Error generating credentials:", error);
      showSnackbar(
        error.message || "Error generating admin credentials",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text).then(
      () => {
        showSnackbar(`${fieldName} copied to clipboard`, "success");
      },
      (err) => {
        showSnackbar(`Failed to copy ${fieldName}`, "error");
      }
    );
  };

  const maskValue = (value: any) => {
    if (!value) return "";
    return "•".repeat(Math.min(value.length, 50));
  };

  return (
    <ContentShell title="Admin Credentials Generator">
      <Box className={classes.container}>
        <Paper className={classes.paper}>
          <Box className={classes.header}>
            <Typography variant="h5" gutterBottom>
              Generate Admin Credentials
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Generate JWT token and internal key for CMS Manager to access
              admin APIs
            </Typography>
          </Box>

          <Alert severity="warning" className={classes.warningBox}>
            <WarningAmberIcon />
            <Box>
              <AlertTitle>Security Notice</AlertTitle>
              <Typography variant="body2">
                These credentials are <strong>highly sensitive</strong> and
                provide full admin access. They will only be displayed once.
                Copy them immediately and store them securely.
              </Typography>
            </Box>
          </Alert>

          {!credentials ? (
            <CustomButton
              label={loading ? "Generating..." : "Generate Credentials"}
              onClick={handleGenerateCredentials}
              disabled={loading}
              className={classes.generateButton}
            />
          ) : (
            <Box className={classes.credentialsBox}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  marginBottom: 2,
                }}
              >
                <CheckCircleIcon color="success" />
                <Typography variant="h6" color="success.main">
                  Credentials Generated Successfully
                </Typography>
              </Box>

              {/* JWT Token Field */}
              <Box className={classes.fieldContainer}>
                <TextField
                  fullWidth
                  label="Admin JWT Token"
                  value={
                    showToken
                      ? credentials?.token || ""
                      : maskValue(credentials?.token || "")
                  }
                  InputProps={{
                    readOnly: true,
                    className: !showToken ? classes.hiddenValue : "",
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowToken(!showToken)}
                          edge="end"
                        >
                          {showToken ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                        <IconButton
                          onClick={() =>
                            handleCopyToClipboard(credentials?.token, "Token")
                          }
                          className={classes.copyButton}
                          edge="end"
                          disabled={!credentials?.token}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  size="small"
                />
              </Box>

              {/* Internal Key Field */}
              <Box className={classes.fieldContainer}>
                <TextField
                  fullWidth
                  label="Internal Key"
                  value={
                    showInternalKey
                      ? credentials?.internalKey || ""
                      : maskValue(credentials?.internalKey || "")
                  }
                  InputProps={{
                    readOnly: true,
                    className: !showInternalKey ? classes.hiddenValue : "",
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowInternalKey(!showInternalKey)}
                          edge="end"
                        >
                          {showInternalKey ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                        <IconButton
                          onClick={() =>
                            handleCopyToClipboard(
                              credentials?.internalKey,
                              "Internal Key"
                            )
                          }
                          className={classes.copyButton}
                          edge="end"
                          disabled={!credentials?.internalKey}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  size="small"
                />
              </Box>

              {/* Expiry Information */}
              {credentials?.expiresIn && (
                <Box className={classes.expiryInfo}>
                  <Typography variant="body2" color="primary">
                    <strong>Expires in:</strong> {credentials.expiresIn}
                  </Typography>
                </Box>
              )}

              <Alert severity="info" sx={{ marginTop: 2 }}>
                <AlertTitle>Usage Instructions</AlertTitle>
                <Typography variant="body2">
                  Include these headers in your API requests:
                  <br />
                  <code>Authorization: Bearer [JWT Token]</code>
                  <br />
                  <code>internal-key: [Internal Key]</code>
                </Typography>
              </Alert>

              <CustomButton
                label="Generate New Credentials"
                onClick={handleGenerateCredentials}
                disabled={loading}
                className={classes.generateButton}
                variant="outlined"
              />
            </Box>
          )}
        </Paper>
      </Box>
    </ContentShell>
  );
};

export default AdminCredentials;
